/**
 * Learning Center API: the in-app inbox (plan docs/plans/lms-plan.md 9.2,
 * 9.4, Appendix A "Notifications and summary"; 18.18, 18.21; WP-33's routes,
 * WP-41's client). Every route reads or writes only the caller's own rows in
 * the caller's network (G14, 4.9).
 *
 *   GET  /me/notifications?unread=1&limit=&offset=  -> { items, page } unread first, then newest first
 *   GET  /me/notifications/unread-count             -> { unread }
 *   POST /me/notifications/:id/read                 -> the notification (404 when it is not the caller's)
 *   POST /me/notifications/read-all                 -> { updated } (this network only, 18.21)
 *
 * The rows carry no email bookkeeping and no dedupe key (18.18: the inbox
 * projection). `url` is a path inside the module (9.2), for example
 * /lms/records or /lms/courses/<id>.
 */
import api from '../../services/api';
import type { LmsList } from './client';

/** lms_notifications.kind (5.8). */
export type NotificationKind =
  | 'assigned'
  | 'due_soon'
  | 'overdue'
  | 'expired'
  | 'campaign'
  | 'certificate_issued'
  | 'signoff_requested'
  | 'signoff_done'
  | 'external_cert_reviewed'
  | 'onboarding_started'
  | 'onboarding_item_due'
  | 'onboarding_item_verify'
  | 'version_published'
  | 'admin_digest'
  | 'waived';

/** One inbox row (WP-33 projectNotification). */
export interface LmsNotification {
  id: string;
  kind: NotificationKind | string;
  title: string;
  body: string;
  url: string;
  subject_type: string | null;
  subject_id: string | null;
  read_at: string | null;
  created_at: string | null;
}

const enc = encodeURIComponent;

/** GET /api/lms/me/notifications (Appendix A: limit at most 200). */
export async function listMyNotifications(
  options: { unreadOnly?: boolean; limit?: number; offset?: number } = {}
): Promise<LmsList<LmsNotification>> {
  const params: Record<string, string | number> = {};
  if (options.unreadOnly) params.unread = '1';
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<LmsNotification>>('/api/lms/me/notifications', { params });
  return response.data;
}

/** GET /api/lms/me/notifications/unread-count */
export async function getUnreadCount(): Promise<number> {
  const response = await api.get<{ unread: number }>('/api/lms/me/notifications/unread-count');
  const n = response.data?.unread;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/** POST /api/lms/me/notifications/:id/read -> the row, now read. */
export async function markNotificationRead(id: string): Promise<LmsNotification> {
  const response = await api.post<LmsNotification>(`/api/lms/me/notifications/${enc(id)}/read`, {});
  return response.data;
}

/** POST /api/lms/me/notifications/read-all -> { updated } */
export async function markAllNotificationsRead(): Promise<{ updated: number }> {
  const response = await api.post<{ updated: number }>('/api/lms/me/notifications/read-all', {});
  return response.data;
}
